import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useId } from 'react';
import { MapPin, Clock, GraduationCap, Send, Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ThemeToggle } from '@/components/auth/theme-toggle';
import { TooltipProvider } from '@/components/ui/tooltip';
import { HyggeLogo } from '@/components/icons/hygge-logo';
import { sanitizeHTML } from '@/lib/sanitize-html';
import { getPublicVacancy, submitApplication, listPublicVacancies, type PublicVacancyListItem } from '@/lib/recruitment';
import type { PublicVacancy, ApplicationFormField } from '@/types/recruitment';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/careers/$slug')({
  component: CareerPage,
});

// ── Page ─────────────────────────────────────────────────────

function CareerPage() {
  const { slug } = Route.useParams();
  const [vacancy, setVacancy] = useState<PublicVacancy | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getPublicVacancy(slug)
      .then(setVacancy)
      .catch(() => setError('Vacancy not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <PageShell>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-hygge" />
        </div>
      </PageShell>
    );
  }

  if (error || !vacancy) {
    return (
      <PageShell>
        <div className="flex flex-col items-center justify-center py-24 gap-4">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <AlertCircle className="h-8 w-8 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">Vacancy Not Found</h1>
          <p className="text-sm text-muted-foreground">This vacancy may have been closed or the link is incorrect.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-[720px] w-full mx-auto px-6">
        {/* Vacancy Header */}
        <section className="mb-9">
          <h1 className="text-[30px] font-bold tracking-[-0.04em] leading-tight mb-4 wrap-anywhere">
            {vacancy.title}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {vacancy.location && (
              <span
                className="inline-flex max-w-[240px] items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-muted border border-border text-muted-foreground"
                title={vacancy.location.name}
              >
                <MapPin className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="truncate">{vacancy.location.name}</span>
              </span>
            )}
            {vacancy.employmentType && (
              <span
                className="inline-flex max-w-[240px] items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-muted border border-border text-muted-foreground"
                title={vacancy.employmentType.name}
              >
                <Clock className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="truncate">{vacancy.employmentType.name}</span>
              </span>
            )}
            {vacancy.seniorityLevel && (
              <span
                className="inline-flex max-w-[240px] items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-muted border border-border text-muted-foreground"
                title={vacancy.seniorityLevel.name}
              >
                <GraduationCap className="h-3.5 w-3.5 shrink-0 opacity-60" />
                <span className="truncate">{vacancy.seniorityLevel.name}</span>
              </span>
            )}
          </div>

          {/* Skills */}
          {vacancy.skills?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {vacancy.skills.map((skill) => (
                <span
                  key={skill.id}
                  className="inline-flex max-w-[200px] items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-hygge/10 text-hygge"
                  title={skill.name}
                >
                  <span className="truncate">{skill.name}</span>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* Job Description */}
        {vacancy.descriptionHtml && (
          <section className="mb-10">
            <style dangerouslySetInnerHTML={{ __html: `
              .career-desc h2 { font-size:18px; font-weight:650; letter-spacing:-0.02em; margin-top:32px; margin-bottom:12px; color:hsl(var(--foreground)); }
              .career-desc h2:first-child { margin-top:0; }
              .career-desc h2::before { content:''; display:inline-block; width:3px; height:18px; background:hsl(var(--hygge)); border-radius:2px; margin-right:10px; vertical-align:middle; position:relative; top:-1px; }
              .career-desc p { font-size:14.5px; line-height:1.75; color:hsl(var(--muted-foreground)); margin-bottom:16px; }
              .career-desc ul { list-style:none; margin-bottom:16px; display:flex; flex-direction:column; gap:2px; padding-left:0; }
              .career-desc ul li { font-size:14px; line-height:1.55; color:hsl(var(--muted-foreground)); padding-left:22px; position:relative; }
              .career-desc ul li p { margin-bottom:0; }
              .career-desc ul li::before { content:''; position:absolute; left:2px; top:9px; width:6px; height:6px; border-radius:50%; background:hsl(var(--hygge)); opacity:0.5; }
              .career-desc ol { margin-bottom:16px; padding-left:22px; }
              .career-desc ol li { font-size:14px; line-height:1.55; color:hsl(var(--muted-foreground)); margin-bottom:4px; }
            `}} />
            <div
              className="career-desc"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(vacancy.descriptionHtml) }}
            />
          </section>
        )}

        {/* Apply Section */}
        <ApplySection vacancy={vacancy} />

        {/* Other Positions */}
        <OtherPositions currentSlug={vacancy.publicSlug} />
      </div>
    </PageShell>
  );
}

// ── Shell ────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background flex flex-col">
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 border-b border-border bg-background/85 backdrop-blur-xl">
          <div className="max-w-[1080px] mx-auto px-8 flex items-center justify-between h-[60px]">
            <a href="#" className="flex items-center no-underline">
              <HyggeLogo className="h-8" />
            </a>
            <ThemeToggle />
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 pt-10 pb-0">
          {children}
        </main>

        {/* Footer */}
        <footer className="text-center py-7 border-t border-border text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} Hygge Software. All rights reserved.
        </footer>
      </div>
    </TooltipProvider>
  );
}

// ── Apply Section ────────────────────────────────────────────

function ApplySection({ vacancy }: { vacancy: PublicVacancy }) {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const fields = vacancy.applicationForm?.fields ?? [];
  const activeFields = fields.filter((f) => f.active);

  if (activeFields.length === 0) {
    return (
      <div className="rounded-lg border bg-muted/50 p-6 text-center text-muted-foreground text-sm mb-10">
        Applications are not being accepted at this time.
      </div>
    );
  }

  if (submitted) {
    return (
      <section className="text-center py-10 mb-14 border-t border-b border-border">
        <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
          <CheckCircle className="h-8 w-8 text-emerald-500" />
        </div>
        <h2 className="text-xl font-semibold tracking-tight mb-2.5">Thank you!</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">
          Your application has been received. We will contact you shortly.
        </p>
      </section>
    );
  }

  return (
    <>
      <section className="text-center py-9 mb-10 border-t border-b border-border">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={cn(
            'inline-flex items-center gap-2 px-10 py-3.5 rounded-xl',
            'bg-cta text-cta-foreground font-semibold text-[15px] tracking-[-0.01em]',
            'shadow-[0_1px_3px_rgba(33,206,153,0.3),0_4px_12px_rgba(33,206,153,0.15)]',
            'hover:brightness-110 hover:-translate-y-0.5',
            'hover:shadow-[0_2px_6px_rgba(33,206,153,0.35),0_8px_20px_rgba(33,206,153,0.2)]',
            'active:translate-y-0 transition-all duration-200 cursor-pointer',
          )}
        >
          <Send className="h-[18px] w-[18px]" />
          Apply for this position
        </button>
        <p className="text-xs text-muted-foreground mt-2.5">Application takes about 2 minutes</p>
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[17px] font-semibold tracking-[-0.02em] wrap-anywhere pr-6">
              Apply for: {vacancy.title}
            </DialogTitle>
          </DialogHeader>
          <ApplicationForm
            vacancy={vacancy}
            onSuccess={() => {
              setOpen(false);
              setSubmitted(true);
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Application Form ─────────────────────────────────────────

// ── Other Positions ──────────────────────────────────────────

function OtherPositions({ currentSlug }: { currentSlug: string }) {
  const [positions, setPositions] = useState<PublicVacancyListItem[]>([]);

  useEffect(() => {
    listPublicVacancies(currentSlug)
      .then(setPositions)
      .catch(() => {});
  }, [currentSlug]);

  if (positions.length === 0) return null;

  return (
    <section className="mb-14">
      <h2 className="text-xl font-semibold tracking-tight mb-5">Other Open Positions</h2>
      <div className="flex flex-col gap-3">
        {positions.map((pos) => (
          <a
            key={pos.id}
            href={`/careers/${pos.publicSlug}`}
            className="flex items-center justify-between px-5 py-4 rounded-xl border border-border bg-card hover:border-hygge hover:bg-hygge/5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 no-underline text-inherit"
          >
            <div className="min-w-0">
              <h3
                className="text-[15px] font-semibold tracking-tight mb-1.5 truncate"
                title={pos.title}
              >
                {pos.title}
              </h3>
              <div className="flex gap-1.5">
                {pos.department && (
                  <span
                    className="inline-block max-w-[200px] truncate text-[11.5px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                    title={pos.department.name}
                  >
                    {pos.department.name}
                  </span>
                )}
                {pos.location && (
                  <span
                    className="inline-block max-w-[200px] truncate text-[11.5px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                    title={pos.location.name}
                  >
                    {pos.location.name}
                  </span>
                )}
              </div>
            </div>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground flex-shrink-0">
              <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
            </svg>
          </a>
        ))}
      </div>
    </section>
  );
}

// ── Application Form ─────────────────────────────────────────

function ApplicationForm({ vacancy, onSuccess }: { vacancy: PublicVacancy; onSuccess: () => void }) {
  const fields: ApplicationFormField[] = vacancy.applicationForm?.fields ?? [];
  const activeFields = fields
    .filter((f) => f.active)
    .slice()
    .sort((a, b) => a.position - b.position);

  const [formData, setFormData] = useState<Record<string, string | boolean>>({});
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const [gdprConsent, setGdprConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (key: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleFileChange = (key: string, file: File | null) => {
    setFiles((prev) => ({ ...prev, [key]: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSubmitting(true);

    try {
      const fd = new FormData();
      const answers: Record<string, string | boolean> = {};

      for (const field of activeFields) {
        if (field.type === 'FILE') {
          const file = files[field.key];
          if (file) fd.append(field.key, file);
        } else if (field.type === 'CHECKBOX') {
          answers[field.key] = formData[field.key] === true;
        } else {
          const value = formData[field.key];
          if (typeof value === 'string' && value.trim()) {
            answers[field.key] = value;
          }
        }
      }

      fd.append('answers', JSON.stringify(answers));
      fd.append('gdprConsent', 'true');

      await submitApplication(vacancy.publicSlug, fd);
      onSuccess();
    } catch (err: any) {
      setSubmitError(err?.response?.data?.message || 'Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = () => {
    for (const field of activeFields) {
      if (!field.required) continue;
      if (field.type === 'FILE') {
        if (!files[field.key]) return false;
      } else if (field.type === 'CHECKBOX') {
        if (formData[field.key] !== true) return false;
      } else {
        const v = formData[field.key];
        if (typeof v !== 'string' || !v.trim()) return false;
      }
    }
    for (const field of activeFields) {
      if (field.type !== 'NUMBER') continue;
      const v = formData[field.key];
      if (typeof v === 'string' && v.trim()) {
        const n = Number(v);
        if (!Number.isFinite(n) || n < 0) return false;
      }
    }
    return gdprConsent;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5 pt-1 min-w-0">
      {activeFields.map((field) => (
        <FormField
          key={field.key}
          field={field}
          value={formData[field.key] ?? (field.type === 'CHECKBOX' ? false : '')}
          onChange={(v) => handleChange(field.key, v)}
          file={field.type === 'FILE' ? files[field.key] ?? null : undefined}
          onFileChange={field.type === 'FILE' ? (f) => handleFileChange(field.key, f) : undefined}
        />
      ))}

      {/* GDPR Consent */}
      <div className="flex items-start gap-2.5 pt-1">
        <Checkbox
          id="gdpr"
          checked={gdprConsent}
          onCheckedChange={(checked) => setGdprConsent(checked === true)}
          className="mt-0.5"
        />
        <label htmlFor="gdpr" className="text-[13px] text-muted-foreground leading-relaxed cursor-pointer">
          I consent to the processing of my personal data in accordance with the{' '}
          <a
            href="https://hygge.software/privacy-policy/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-hygge underline underline-offset-2"
          >
            Privacy Policy
          </a>{' '}*
        </label>
      </div>

      {submitError && (
        <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
          {submitError}
        </div>
      )}

      <Button
        type="submit"
        disabled={!isValid() || submitting}
        className="w-full bg-cta hover:bg-cta/90 text-cta-foreground font-semibold text-[14.5px] py-5 mt-1"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Submitting...
          </>
        ) : (
          'Submit Application'
        )}
      </Button>
    </form>
  );
}

// ── Form Field Renderer ──────────────────────────────────────

interface FormFieldProps {
  field: ApplicationFormField;
  value: string | boolean;
  onChange: (value: string | boolean) => void;
  file?: File | null;
  onFileChange?: (file: File | null) => void;
}

function FormField({ field, value, onChange, file, onFileChange }: FormFieldProps) {
  const isRequired = field.required;
  const inputId = useId();
  const labelText = field.label;
  // wrap-anywhere: a field label is whatever the recruiter typed (up to 120 characters, possibly
  // without a break opportunity), and it must stay readable inside the apply dialog.
  const labelWithAsterisk = (
    <span className="wrap-anywhere">
      {labelText} {isRequired && <span className="text-destructive">*</span>}
    </span>
  );

  if (field.type === 'FILE') {
    return (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{labelWithAsterisk}</label>
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-7 text-center cursor-pointer transition-all duration-200',
            'hover:border-hygge/50 hover:bg-hygge/5',
            file && 'border-hygge/30 bg-hygge/5',
          )}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          <input
            id={inputId}
            type="file"
            accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            className="hidden"
            onChange={(e) => onFileChange?.(e.target.files?.[0] ?? null)}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2.5 min-w-0">
              <Upload className="h-4 w-4 text-hygge flex-shrink-0" />
              <span
                className="text-sm font-medium truncate min-w-0"
                title={file.name}
              >
                {file.name}
              </span>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-destructive transition-colors ml-1 flex-shrink-0"
                onClick={(e) => {
                  e.stopPropagation();
                  onFileChange?.(null);
                }}
              >
                Remove
              </button>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Upload className="h-9 w-9 mx-auto text-muted-foreground" />
              <p className="text-[13.5px] text-muted-foreground">
                Drop file here or <span className="text-hygge font-semibold">browse</span>
              </p>
              <p className="text-xs text-muted-foreground/70">PDF or Docx, max 10MB</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (field.type === 'LONG_TEXT') {
    return (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{labelWithAsterisk}</label>
        <textarea
          className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-[13.5px] shadow-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-hygge focus-visible:border-hygge transition-colors"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange(e.target.value)}
          required={isRequired}
        />
      </div>
    );
  }

  if (field.type === 'CHECKBOX') {
    return (
      <div className="flex items-start gap-2.5">
        <Checkbox
          id={inputId}
          checked={value === true}
          onCheckedChange={(c) => onChange(c === true)}
          className="mt-0.5"
        />
        <label
          htmlFor={inputId}
          className="text-[13px] text-muted-foreground leading-relaxed cursor-pointer"
        >
          {labelWithAsterisk}
        </label>
      </div>
    );
  }

  if (field.type === 'DROPDOWN') {
    const strValue = typeof value === 'string' ? value : '';
    return (
      <div className="space-y-1.5">
        <label className="text-xs font-medium text-muted-foreground">{labelWithAsterisk}</label>
        <Select value={strValue} onValueChange={onChange}>
          <SelectTrigger className="h-9 text-[13.5px] focus:ring-hygge">
            <SelectValue placeholder="Select an option..." />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((opt) => (
              <SelectItem
                key={opt}
                value={opt}
                className="[&>span:last-child]:truncate"
                title={opt}
              >
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const inputType =
    field.type === 'EMAIL' ? 'email'
    : field.type === 'PHONE' ? 'tel'
    : field.type === 'NUMBER' ? 'number'
    : field.type === 'URL' ? 'url'
    : 'text';

  const numberInvalid =
    field.type === 'NUMBER' &&
    typeof value === 'string' &&
    value.trim() !== '' &&
    (() => {
      const n = Number(value);
      return !Number.isFinite(n) || n < 0;
    })();

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">{labelWithAsterisk}</label>
      <Input
        type={inputType}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(e.target.value)}
        required={isRequired}
        min={field.type === 'NUMBER' ? 0 : undefined}
        step={field.type === 'NUMBER' ? 1 : undefined}
        aria-invalid={numberInvalid || undefined}
        className={cn(
          'text-[13.5px] focus-visible:ring-hygge focus-visible:border-hygge',
          numberInvalid &&
            'border-destructive focus-visible:ring-destructive focus-visible:border-destructive',
        )}
      />
      {numberInvalid && (
        <p className="text-xs text-destructive">Must be a non-negative number.</p>
      )}
    </div>
  );
}
